import { useState } from 'react'
import { Box, Flex, Heading, Text, HStack, Select, Button, Spinner } from '@chakra-ui/react'
import { Download } from 'lucide-react'
import type { ExportFormat } from '@/components/common/ExportButton'
import ReportFilters from './ReportFilters'
import { helpdeskReportService, type ReportFilters as Filters } from '@/lib/services/helpdeskReportService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { enrichBlobAxiosError } from '@/lib/utils/blobDownload'

type Props = {
  title: string
  subtitle?: string
  reportType: string
  filters: Filters
  onFiltersChange: (f: Filters) => void
  showGroupBy?: boolean
  exportDisabled?: boolean
}

const FORMAT_OPTIONS: { value: ExportFormat; label: string }[] = [
  { value: 'csv', label: 'CSV' },
  { value: 'excel', label: 'Excel' },
  { value: 'pdf', label: 'PDF' },
]

export default function ReportPageHeader({
  title,
  subtitle,
  reportType,
  filters,
  onFiltersChange,
  showGroupBy,
  exportDisabled,
}: Props) {
  const excelDefaultTypes = ['agent-productivity', 'ticket-productivity', 'contact-center-productivity']
  const [format, setFormat] = useState<ExportFormat>(
    excelDefaultTypes.includes(reportType) ? 'excel' : 'csv'
  )
  const [isExporting, setIsExporting] = useState(false)
  const { showError, showSuccess } = useToastNotification()

  const handleDownload = async () => {
    setIsExporting(true)
    try {
      await helpdeskReportService.download(reportType, filters, format)
      const label = FORMAT_OPTIONS.find((o) => o.value === format)?.label ?? format
      showSuccess('Report downloaded', `${label} export started.`)
    } catch (err) {
      showError(await enrichBlobAxiosError(err))
    } finally {
      setIsExporting(false)
    }
  }

  const disabled = exportDisabled || isExporting

  return (
    <Box mb={6}>
      <Flex justify="space-between" align="flex-start" gap={4} flexWrap="wrap">
        <Box>
          <Heading size="md">{title}</Heading>
          {subtitle && (
            <Text fontSize="sm" color="gray.500" mt={1}>
              {subtitle}
            </Text>
          )}
        </Box>
        <HStack spacing={2} flexShrink={0}>
          <Select
            size="sm"
            width="120px"
            value={format}
            onChange={(e) => setFormat(e.target.value as ExportFormat)}
            isDisabled={disabled}
            aria-label="Export format"
          >
            {FORMAT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            leftIcon={isExporting ? <Spinner size="sm" /> : <Download size={16} />}
            onClick={() => void handleDownload()}
            isDisabled={disabled}
          >
            Download
          </Button>
        </HStack>
      </Flex>
      <Box mt={4}>
        <ReportFilters filters={filters} onChange={onFiltersChange} showGroupBy={showGroupBy} />
      </Box>
    </Box>
  )
}
